/**
 * SLACountdown — Time-pressure widget for task SLA visualization.
 *
 * Calculates remaining time based on:
 * - estimatedDurationMs from the task (SLA target)
 * - Elapsed time since startedAt (or createdAt as fallback)
 *
 * Color progression: Green (>50% remaining) → Yellow (25-50%) → Red (<25%)
 * Uses Mantine RingProgress for compact, visual representation.
 *
 * Renders nothing if no estimatedDurationMs is present (graceful degradation).
 *
 * DUMB FRONTEND: All timing data from TaskFieldsFragment.
 */

import { useMemo } from 'react';
import {
  Group,
  RingProgress,
  Text,
  Tooltip,
  Box,
  ThemeIcon,
} from '@mantine/core';
import { IconClock, IconAlertTriangle } from '@tabler/icons-react';
import { GqlTaskStatus, type TaskFieldsFragment } from '@/generated/graphql';

// ============================================================================
// COMPONENT
// ============================================================================

interface SLACountdownProps {
  task: TaskFieldsFragment;
}

export function SLACountdown({ task }: SLACountdownProps) {
  const slaData = useMemo(() => {
    // Only show SLA if we have an estimated duration
    if (!task.estimatedDurationMs) return null;

    const estimatedMs = parseInt(task.estimatedDurationMs, 10);
    if (isNaN(estimatedMs) || estimatedMs <= 0) return null;

    // If task is completed, show final result
    if (task.status === GqlTaskStatus.COMPLETED && task.durationMs) {
      const actualMs = parseInt(task.durationMs, 10);
      const overUnder = estimatedMs - actualMs;
      return {
        percentRemaining: overUnder >= 0 ? 100 : 0,
        remainingLabel: overUnder >= 0
          ? `Finished ${formatMs(Math.abs(overUnder))} early`
          : `Finished ${formatMs(Math.abs(overUnder))} late`,
        isComplete: true,
        isOverdue: overUnder < 0,
        color: overUnder >= 0 ? 'green' : 'red',
        ringValue: 100,
      };
    }

    // If task is cancelled or compensated, no SLA needed
    if (
      task.status === GqlTaskStatus.CANCELLED ||
      task.status === GqlTaskStatus.COMPENSATED
    ) {
      return null;
    }

    // Calculate elapsed time
    const startTime = task.startedAt ?? task.createdAt;
    const elapsedMs = Date.now() - new Date(startTime).getTime();
    const remainingMs = estimatedMs - elapsedMs;
    const percentRemaining = Math.max(
      0,
      Math.min(100, (remainingMs / estimatedMs) * 100)
    );

    // Determine color based on remaining percentage
    let color: string;
    if (percentRemaining > 50) {
      color = 'green';
    } else if (percentRemaining > 25) {
      color = 'yellow';
    } else {
      color = 'red';
    }

    const isOverdue = remainingMs <= 0;

    return {
      percentRemaining,
      remainingLabel: isOverdue
        ? `Overdue by ${formatMs(Math.abs(remainingMs))}`
        : `${formatMs(remainingMs)} remaining`,
      isComplete: false,
      isOverdue,
      color,
      ringValue: isOverdue ? 100 : 100 - percentRemaining,
    };
  }, [task]);

  // Graceful degradation: render nothing if no SLA data
  if (!slaData) return null;

  return (
    <Tooltip
      label={slaData.remainingLabel}
      position="bottom"
      withArrow
    >
      <Box
        px="sm"
        py={6}
        style={{
          background: slaData.isOverdue
            ? 'var(--mantine-color-red-0)'
            : slaData.color === 'yellow'
              ? 'var(--mantine-color-yellow-0)'
              : 'var(--mantine-color-green-0)',
          borderRadius: 'var(--mantine-radius-sm)',
          border: slaData.isOverdue
            ? '1px solid var(--mantine-color-red-3)'
            : '1px solid var(--mantine-color-gray-2)',
        }}
      >
        <Group gap="xs" wrap="nowrap">
          <RingProgress
            size={36}
            thickness={4}
            roundCaps
            sections={[
              {
                value: slaData.ringValue,
                color: slaData.isOverdue
                  ? 'red'
                  : slaData.color === 'yellow'
                    ? 'yellow.6'
                    : 'green',
              },
            ]}
            label={
              <ThemeIcon
                size={20}
                radius="xl"
                variant="light"
                color={slaData.isOverdue ? 'red' : slaData.color}
              >
                {slaData.isOverdue ? (
                  <IconAlertTriangle size={12} />
                ) : (
                  <IconClock size={12} />
                )}
              </ThemeIcon>
            }
          />
          <div>
            <Text size="xs" fw={600} c={slaData.isOverdue ? 'red' : undefined}>
              {slaData.isComplete ? 'SLA Result' : 'SLA Countdown'}
            </Text>
            <Text size="xs" c={slaData.isOverdue ? 'red.7' : 'dimmed'}>
              {slaData.remainingLabel}
            </Text>
          </div>
        </Group>
      </Box>
    </Tooltip>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

function formatMs(ms: number): string {
  const totalMinutes = Math.floor(ms / 60_000);
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours < 24) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
}
